import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { ScoringConfigType } from "app/pages/sales-intelligence/domain/leads/types/scoring-config";
import { ScoringScopeBadge } from "app/pages/sales-intelligence/common-components/scoring/ScoringScopeBadge/ScoringScopeBadge";
import type { TeamListItem } from "app/pages/sales-intelligence/domain/teams/types/team-list-item";

import type { ImpactedAsset, ImpactedAssetGroup } from "../../types/impactReport";
import { impactedAssetGroupMap } from "../../types/impactReport";
import { industryTransitionKeys } from "../../utils/i18n";
import { parseImpactReportTeamId } from "../../utils/parseImpactReportTeamId";

import { TerritoryMembersPreview } from "./TerritoryMembersPreview";

type ImpactedAssetRowMetadataProps = {
    readonly asset: ImpactedAsset;
    readonly group: ImpactedAssetGroup;
    readonly teamsById?: ReadonlyMap<number, TeamListItem>;
};

const toScoringScope = (isAccountLevel: boolean | undefined): ScoringConfigType | undefined => {
    if (isAccountLevel === true) {
        return ScoringConfigType.Account;
    }

    if (isAccountLevel === false) {
        return ScoringConfigType.User;
    }

    return undefined;
};

export const ImpactedAssetRowMetadata = ({ asset, group, teamsById }: ImpactedAssetRowMetadataProps) => {
    const { translate } = useTranslate();

    if (group === impactedAssetGroupMap.territories) {
        const teamId = parseImpactReportTeamId(asset.id);
        const team = teamId === undefined ? undefined : teamsById?.get(teamId);

        if (team === undefined) {
            return null;
        }

        return (
            <Flex
                alignItems="center"
                flexShrink={0}
                data-automation="industry-transition-impact-report-row-metadata"
            >
                <TerritoryMembersPreview team={team} />
            </Flex>
        );
    }

    if (group === impactedAssetGroupMap.leadScoringRules) {
        const scope = toScoringScope(asset.isAccountLevel);

        if (scope === undefined) {
            return null;
        }

        return (
            <Flex
                gap="8px"
                alignItems="center"
                flexShrink={0}
                data-automation="industry-transition-impact-report-row-metadata"
            >
                <ScoringScopeBadge
                    scope={scope}
                    showSharedTooltip={false}
                />
                {asset.isAccountLevel === false && asset.ownerName !== undefined ? (
                    <>
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.carbon[300]}
                        >
                            {translate(industryTransitionKeys.reportRowOwner)}
                        </Text>
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.carbon[500]}
                        >
                            {asset.ownerName}
                        </Text>
                    </>
                ) : null}
            </Flex>
        );
    }

    if (asset.ownerName === undefined) {
        return null;
    }

    return (
        <Flex
            gap="8px"
            alignItems="center"
            flexShrink={0}
            data-automation="industry-transition-impact-report-row-metadata"
        >
            <Text
                fontVariant="body2"
                color={colorsPalettes.carbon[300]}
            >
                {translate(industryTransitionKeys.reportRowOwner)}
            </Text>
            <Text
                fontVariant="body2"
                color={colorsPalettes.carbon[500]}
            >
                {asset.ownerName}
            </Text>
        </Flex>
    );
};
